import React from 'react';

const OffersFilterPanel = ({
  games,
  selectedGame,
  setSelectedGame,
  selectedGames,
  setSelectedGames,
  showOnlyActive,
  setShowOnlyActive,
  showOnlyMyOffers,
  setShowOnlyMyOffers,
  showOnlyPersonalOffers,
  setShowOnlyPersonalOffers,
  filterOfferID,
  setFilterOfferID,
  sortPriorities,
  setSortPriorities,
  isOpen,
  togglePanel
}) => {
  // Check if game filter is active (not default "all games")
  const isGameFilterActive = selectedGames !== null && 
    (selectedGames.length === 0 || selectedGames.length !== games.length);
  
  const activeFiltersCount = (
    (selectedGame !== 'All' ? 1 : 0) + 
    (isGameFilterActive ? 1 : 0) +
    (showOnlyActive ? 1 : 0) + 
    (showOnlyMyOffers ? 1 : 0) +
    (showOnlyPersonalOffers ? 1 : 0) +
    (filterOfferID && filterOfferID.trim() !== '' ? 1 : 0)
  );

  const availableSortOptions = [
    { key: 'offerID', label: 'Offer ID (newest first)' },
    { key: 'player2Bet', label: 'Player 2 bet (highest first)' },
    { key: 'betRatio', label: 'Bet ratio (highest first)' },
    { key: 'timeout', label: 'Timeout (shortest first)' }
  ];

  const addSortPriority = (sortKey) => {
    if (!sortPriorities.includes(sortKey)) {
      setSortPriorities([...sortPriorities, sortKey]);
    }
  };

  const removeSortPriority = (sortKey) => {
    setSortPriorities(sortPriorities.filter(key => key !== sortKey));
  };

  const moveSortPriority = (sortKey, direction) => {
    const currentIndex = sortPriorities.indexOf(sortKey);
    if (currentIndex === -1) return;
    
    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= sortPriorities.length) return;
    
    const newPriorities = [...sortPriorities];
    [newPriorities[currentIndex], newPriorities[newIndex]] = [newPriorities[newIndex], newPriorities[currentIndex]];
    setSortPriorities(newPriorities);
  };

  return (
    <div className="mb-4">
      {/* Filter Toggle Button */}
      <button
        onClick={togglePanel}
        className={`inline-flex items-center px-4 py-2 rounded-lg border transition-all duration-200 ${
          isOpen 
            ? 'bg-blue-50 border-blue-300 text-blue-700 shadow-sm' 
            : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
        }`}
      >
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
        Offers
        {activeFiltersCount > 0 && (
          <span className="ml-2 px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded-full">
            {activeFiltersCount}
          </span>
        )}
        <svg className={`w-4 h-4 ml-2 transition-transform duration-200 ${
          isOpen ? 'rotate-180' : ''
        }`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Filter Panel */}
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${
        isOpen ? 'max-h-screen opacity-100' : 'max-h-0 opacity-0'
      }`}>
        <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Left Side - Filters */}
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                </svg>
                Filters
              </h3>
              
              <div className="space-y-4">
                {/* Game Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Game Types
                  </label>
                  <div className="space-y-2">
                    {/* Game Selection Buttons */}
                    <div className="flex flex-wrap gap-1 max-h-32 overflow-y-auto p-1 border border-gray-200 rounded bg-white">
                      <button
                        onClick={() => setSelectedGames(games.map(g => g.name))}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded border"
                      >
                        All
                      </button>
                      <button
                        onClick={() => setSelectedGames([])}
                        className="px-2 py-1 text-xs bg-gray-100 hover:bg-gray-200 rounded border"
                      >
                        None
                      </button>
                      {games.map(game => (
                        <button
                          key={game.gameAddress}
                          onClick={() => {
                            if (selectedGames === null) {
                              // When showing all (default), clicking a game means "show only this game"
                              setSelectedGames([game.name]);
                            } else if (selectedGames.length === 0) {
                              // When showing none, clicking a game means "show only this game"
                              setSelectedGames([game.name]);
                            } else if (selectedGames.includes(game.name)) {
                              // If this game is selected, remove it from selection
                              setSelectedGames(selectedGames.filter(name => name !== game.name));
                            } else {
                              // If this game is not selected, add it to selection
                              setSelectedGames([...selectedGames, game.name]);
                            }
                          }}
                          className={`px-2 py-1 text-xs rounded border ${
                            (selectedGames === null || (selectedGames && selectedGames.length > 0 && selectedGames.includes(game.name)))
                              ? 'bg-blue-100 border-blue-300 text-blue-700'
                              : 'bg-gray-100 hover:bg-gray-200 border-gray-300'
                          }`}
                        >
                          {game.name}
                        </button>
                      ))}
                    </div>
                    
                    {/* Current Selection Display */}
                    {(selectedGames === null || (selectedGames && selectedGames.length > 0)) && (
                      <div className="text-xs text-gray-600">
                        {selectedGames === null ? (
                          <>Selected: {games.length} of {games.length} games (all)</>
                        ) : (
                          <>
                            Selected: {selectedGames.length} of {games.length} games
                            {selectedGames.length <= 10 && (
                              <div className="mt-1 text-gray-500">
                                {selectedGames.join(', ')}
                              </div>
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {/* Offer ID Filter */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Filter by Offer ID
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={filterOfferID || ''}
                      onChange={(e) => {
                        const value = e.target.value;
                        // Only allow numbers
                        if (value === '' || /^\d+$/.test(value)) {
                          setFilterOfferID(value);
                        }
                      }}
                      placeholder="Enter offer ID..."
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                    />
                    {filterOfferID && filterOfferID.trim() !== '' && (
                      <button
                        onClick={() => setFilterOfferID('')}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear filter"
                      >
                        ✕
                      </button>
                    )}
                  </div>
                  {filterOfferID && filterOfferID.trim() !== '' && (
                    <div className="mt-1 text-xs text-blue-600">
                      Showing only offer ID: {filterOfferID}
                    </div>
                  )}
                </div>

                {/* Status Filter */}
                <div>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={showOnlyActive}
                        onChange={(e) => setShowOnlyActive(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      />
                      <span className="ml-2 text-sm text-gray-700">Active only</span>
                    </label>
                  </div>
                </div>

                {/* Ownership Filter */}
                <div>
                  <div className="space-y-2">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={showOnlyMyOffers}
                        onChange={(e) => setShowOnlyMyOffers(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      />
                      <span className="ml-2 text-sm text-gray-700">My offers only</span>
                    </label>
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={showOnlyPersonalOffers}
                        onChange={(e) => setShowOnlyPersonalOffers(e.target.checked)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500 h-4 w-4"
                      />
                      <span className="ml-2 text-sm text-gray-700">Personal offers only</span>
                    </label>
                  </div>
                </div>

                {/* Clear Filters */}
                {activeFiltersCount > 0 && (
                  <div className="pt-2 border-t border-gray-300">
                    <button
                      onClick={() => {
                        setSelectedGame('All');
                        setSelectedGames(games.map(g => g.name));
                        setShowOnlyActive(false);
                        setShowOnlyMyOffers(false);
                        setShowOnlyPersonalOffers(false);
                        setFilterOfferID('');
                      }}
                      className="text-sm text-gray-600 hover:text-gray-800 underline"
                    >
                      Clear all filters
                    </button>
                  </div>
                )}
              </div>
            </div>

            {/* Right Side - Sort Priorities */}
            <div>
              <h3 className="text-lg font-medium text-gray-700 mb-4 flex items-center">
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12" />
                </svg>
                Sort Priority
              </h3>
              
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Sort Priority Order (click to prioritize)
                  </label>
                  <div className="space-y-2">
                    {availableSortOptions.map((option, index) => {
                      // Calculate effective sort order
                      const effectivePriorities = sortPriorities.length > 0 
                        ? [...sortPriorities, ...availableSortOptions.filter(opt => !sortPriorities.includes(opt.key)).map(opt => opt.key)]
                        : availableSortOptions.map(opt => opt.key);
                      
                      const priority = effectivePriorities.indexOf(option.key) + 1;
                      
                      return (
                        <div 
                          key={option.key} 
                          className="flex items-center p-3 rounded border cursor-pointer transition-all bg-white border-gray-200 hover:bg-gray-50"
                          onClick={() => {
                            // Remove from current position if exists
                            const newPriorities = sortPriorities.filter(key => key !== option.key);
                            // Add to front (priority 1)
                            setSortPriorities([option.key, ...newPriorities]);
                          }}
                        >
                          <div className="flex items-center">
                            <div className="w-6 h-6 rounded-full bg-blue-500 border-2 border-blue-500 text-white mr-3 flex items-center justify-center text-xs font-bold">
                              {priority}
                            </div>
                            <span className="text-sm text-gray-700">
                              {option.label}
                            </span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OffersFilterPanel;